import { FC, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import cx from 'classnames'

import {
  BronzeBadgeLarge,
  DefaultBadgeLarge,
  DiamondBadgeLarge,
  GoldenBadgeLarge,
  SilverBadgeLarge,
} from '@/components/atoms/assets/XPBadges'
import { XPScoreBg } from '@/components/atoms/assets/XPScoreBg'

import { Badge } from '@/enums'

export interface XPScoreHeaderProps {
  xp: number
  badge?: Badge
  multiplier?: number
}

export const XPScoreHeader: FC<XPScoreHeaderProps> = ({
  xp,
  badge,
  multiplier,
}) => {
  const { t } = useTranslation('common')

  const renderBadge = () => {
    switch (badge) {
      case Badge.BronzeXPMilestoneBadge:
        return <BronzeBadgeLarge xp={xp} />
      case Badge.SilverXPMilestoneBadge:
        return <SilverBadgeLarge xp={xp} />
      case Badge.GoldXPMilestoneBadge:
        return <GoldenBadgeLarge xp={xp} className="-mb-[2vw]" />
      case Badge.DiamondXPMilestoneBadge:
        return <DiamondBadgeLarge xp={xp} className="-mb-[2vw]" />
      default:
        return <DefaultBadgeLarge xp={xp} />
    }
  }

  const badgeWidth = useMemo(() => {
    switch (badge) {
      case Badge.DiamondXPMilestoneBadge:
        return 'w-[50vw] max-w-[21rem]'
      case Badge.GoldXPMilestoneBadge:
        return 'w-[53vw] max-w-[22.5rem]'
      case Badge.SilverXPMilestoneBadge:
        return 'w-[45vw] max-w-[19rem]'
      case Badge.BronzeXPMilestoneBadge:
      default:
        return 'w-[42vw] max-w-[18rem]'
    }
  }, [badge])

  const rewardBackgroundColor = useMemo(() => {
    switch (badge) {
      case Badge.DiamondXPMilestoneBadge:
        return 'bg-diamond'
      case Badge.GoldXPMilestoneBadge:
        return 'bg-gold'
      case Badge.SilverXPMilestoneBadge:
        return 'bg-silver'
      case Badge.BronzeXPMilestoneBadge:
      default:
        return 'bg-bronze'
    }
  }, [badge])

  return (
    <div className="relative w-full py-[5vw] md:py-[3.125rem]">
      <XPScoreBg className="bg-cool-gray-700 border border-cool-gray rounded-xl w-full h-full" />
      <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center p-[10vw] md:p-[4.25rem]">
        <div className="w-[50%]">
          <div
            className={cx(
              'text-white text-[5vw] md:text-[2.5rem] font-bold absolute -translate-y-1/2',
              { 'top-[50%]': !multiplier, 'top-[39%]': multiplier }
            )}
          >
            <Trans
              t={t}
              i18nKey={'XPWallet.yourWalletValue'}
              components={{ primary: <span className="text-primary" /> }}
            >
              {t('XPWallet.yourWalletValue')}
            </Trans>
          </div>
          {multiplier && (
            <div className="flex absolute bottom-[13%] -translate-y-1/2">
              <div className={`w-[4px] flex mr-2 ${rewardBackgroundColor}`} />
              <div className="flex flex-col text-white font-bold">
                <span className="text-[3.25vw] sm:text-xl">{multiplier}</span>
                <span className="text-[2.5vw] sm:text-base">
                  {t('XPWallet.rewardBonus')}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="w-[50%] flex items-center justify-center relative">
          <div className={`absolute flex items-center z-20 ${badgeWidth}`}>
            {renderBadge()}
          </div>
        </div>
      </div>
    </div>
  )
}
